// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Header from "../Header";
import Footer from "../footer";
import { API_ENDPOINTS } from "../config/api";
import { saveAuthSession } from "../utils/auth";
import "./Auth.css";

export default function AuthPage() {
    const [email, setEmail] = useState("");
    const [emailChecked, setEmailChecked] = useState(false);
    const [userExists, setUserExists] = useState(false);
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [showPasswordRules, setShowPasswordRules] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    // New validation states
    const [emailError, setEmailError] = useState("");
    const [nameError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [isFormValid, setIsFormValid] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [emailChecking, setEmailChecking] = useState(false);
    const [emailFocused, setEmailFocused] = useState(false);
    const [passwordFocused, setPasswordFocused] = useState(false);
    const [nameFocused, setNameFocused] = useState(false);

    const emailInputRef = useRef(null);
    const passwordInputRef = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();

    const from =
        location.state &&
        location.state.from &&
        location.state.from.pathname
        ? location.state.from.pathname
        : "/";

    // Auto-focus email field on page load
    useEffect(() => {
        if (emailInputRef.current) emailInputRef.current.focus();
    }, []);

    // Auto-focus password field when email step completes
    useEffect(() => {
        if (emailChecked && passwordInputRef.current) passwordInputRef.current.focus();
    }, [emailChecked]);

    const handleGoogleResponse = useCallback(async (response) => {
        try {
            const res = await axios.post(
                API_ENDPOINTS.AUTH.GOOGLE,
                { token: response.credential },
                {
                    timeout: 20000, // 20 second timeout
                    withCredentials: true
                }
            );

            const data = res.data;
            const userObj = {
                _id: data.user._id,
                name: data.user.name || "",
                email: data.user.email,
                profileImage: data.user.profileImage || "",
                isSubscribed: data.user.isSubscribed || false,
                role: data.user.role || "user"
            };

            saveAuthSession(data.token, userObj);

            // alert("Google Auth Successful");
            navigate(from, { replace: true });
        } catch (err) {
            console.error("Google Auth Error:", err);
            alert("Google Auth Failed");
        }
    }, [from, navigate]);

    useEffect(() => {
        if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
            client_id:
            "757759688712-gp3v6gdi0ah1dljlou62308cti7c88or.apps.googleusercontent.com",
            callback: handleGoogleResponse,
        });

        window.google.accounts.id.renderButton(
            document.getElementById("googleAuthDiv"),
            { theme: "outline", size: "large" }
        );
        }
    }, [handleGoogleResponse]);

    const checkEmailExists = async () => {
        if (!email) return;
        setEmailChecking(true);
        try {
            const res = await axios.post(
                API_ENDPOINTS.AUTH.CHECK_EMAIL,
                { email },
                {
                    timeout: 20000, // 20 second timeout
                    withCredentials: true
                }
            );
            setUserExists(res.data.exists);
            setEmailChecked(true);
        } catch (err) {
            console.error("Check email error", err);
            
            // Handle different types of errors
            let errorMessage = "Something went wrong checking email";
            
            if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
                errorMessage = "Request timed out. The server may be slow or unavailable. Please try again.";
            } else if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
                errorMessage = "Network error: Unable to connect to server. Please check your internet connection.";
            } else if (err.response) {
                // Server responded with error status
                const status = err.response.status;
                const responseMessage = (err.response.data && err.response.data.message) ? err.response.data.message : null;
                
                if (status === 503 || status === 504) {
                    errorMessage = responseMessage || "Database connection issue. Please try again in a moment.";
                } else if (status === 500) {
                    errorMessage = "Server error. Please try again later.";
                } else if (responseMessage) {
                    errorMessage = responseMessage;
                } else {
                    errorMessage = `Error: ${status}`;
                }
            } else if (err.request) {
                // Request was made but no response received
                errorMessage = "No response from server. Please check your connection and try again.";
            }
            
            alert(errorMessage);
        } finally {
            setEmailChecking(false);
        }
    };

    // Validation functions
    const validateEmail = (value) => {
        if (!value) {
            setEmailError("Email is required");
            return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            setEmailError("Please enter a valid email address");
            return false;
        }
        setEmailError("");
        return true;
    };

    const validatePassword = (value) => {
        if (!value) {
            setPasswordError("Password is required");
            return false;
        }

        if (userExists) {
            setPasswordError("");
            return true;
        }
        
        const conditions = {
            minLength: value.length >= 8,
            hasNumber: /\d/.test(value),
            hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(value)
        };
        
        const errors = [];
        if (!conditions.minLength) errors.push("At least 8 characters");
        if (!conditions.hasNumber) errors.push("Contains a number");
        if (!conditions.hasSpecialChar) errors.push("Contains a special character");
        
        if (errors.length > 0) {
            setPasswordError(`Password must: ${errors.join(", ")}`);
            return false;
        }
        
        setPasswordError("");
        return true;
    };

    // Check form validity whenever inputs change
    useEffect(() => {
        if (emailChecked) {
            const emailValid = !emailError && email;
            const nameValid = userExists ? true : (!nameError && name.trim().length >= 2);
            const passwordValid = !passwordError && password;
            
            setIsFormValid(emailValid && nameValid && passwordValid);
        }
    }, [email, name, password, emailChecked, userExists, emailError, nameError, passwordError]);

    const handleAuthSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (userExists) {
                const res = await axios.post(
                    API_ENDPOINTS.AUTH.LOGIN,
                    { email, password },
                    {
                        timeout: 20000, // 20 second timeout
                        withCredentials: true
                    }
                );
                const data = res.data;
                saveAuthSession(data.token, data.user);
                
                setSuccess(true);
                setTimeout(() => {
                    // Role-based redirect - admin users go to home page
                    navigate(from, { replace: true });
                }, 1500);
            } else {
                const res = await axios.post(
                    API_ENDPOINTS.AUTH.REGISTER,
                    { name, email, password },
                    {
                        timeout: 20000, // 20 second timeout
                        withCredentials: true
                    }
                );
                // Auto login after register
                const loginRes = await axios.post(
                    API_ENDPOINTS.AUTH.LOGIN,
                    { email, password },
                    {
                        timeout: 20000, // 20 second timeout
                        withCredentials: true
                    }
                );
                const loginData = loginRes.data;
                saveAuthSession(loginData.token, loginData.user);
                
                setSuccess(true);
                setTimeout(() => {
                    // Role-based redirect for new registrations - admin users go to home page
                    navigate(from, { replace: true });
                }, 1500);
            }
        } catch (err) {
            console.error("Auth Error:", err);
            
            // Handle different types of errors
            let errorMessage = "Authentication Failed";
            
            if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
                errorMessage = "Request timed out. The server may be slow or unavailable. Please try again.";
            } else if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
                errorMessage = "Network error: Unable to connect to server. Please check your internet connection.";
            } else if (err.response) {
                // Server responded with error status
                const status = err.response.status;
                const responseMessage = (err.response.data && err.response.data.message) ? err.response.data.message : null;
                
                if (status === 503 || status === 504) {
                    errorMessage = responseMessage || "Database connection issue. Please try again in a moment.";
                } else if (status === 500) {
                    errorMessage = "Server error. Please try again later.";
                } else if (responseMessage) {
                    errorMessage = responseMessage;
                } else {
                    errorMessage = `Error: ${status}`;
                }
            } else if (err.request) {
                // Request was made but no response received
                errorMessage = "No response from server. Please check your connection and try again.";
            }
            
            alert(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const isMinLength = password.length >= 8;
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return (
        <>
        <Helmet>
            <title>Sign In | Hari Vishnu</title>
            <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <link rel="stylesheet" href="https://cdn-uicons.flaticon.com/3.0.0/uicons-regular-rounded/css/uicons-regular-rounded.css" />
        <link rel="stylesheet" href="https://cdn-uicons.flaticon.com/3.0.0/uicons-thin-rounded/css/uicons-thin-rounded.css" />
        <div className="flex flex-col min-h-screen transition-colors duration-300">
            <Header />
            <main className="flex-1 relative bg-sky-50 overflow-hidden grid place-items-center" style={{ minHeight: 'calc(100vh - var(--site-header-height, 64px))' }}>
                <div className="waves-container pointer-events-none absolute bottom-0 left-0 w-full h-[140px] z-0">
                    <svg className="wave wave-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
                        <path fill="#47b5ff" fillOpacity="0.4" d="M0,96L48,112C96,128,192,160,288,165.3C384,171,480,149,576,133.3C672,117,768,107,864,112C960,117,1056,139,1152,149.3C1248,160,1344,160,1392,160L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
                    </svg>
                    <svg className="wave wave-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
                        <path fill="#2563eb" fillOpacity="0.5" d="M0,192L48,197.3C96,203,192,213,288,213.3C384,213,480,203,576,197.3C672,192,768,192,864,197.3C960,203,1056,213,1152,213.3C1248,213,1344,203,1392,197.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
                    </svg>
                    <svg className="wave wave-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
                        <path fill="#1e40af" fillOpacity="0.6" d="M0,256L48,245.3C96,235,192,213,288,197.3C384,181,480,171,576,181.3C672,192,768,224,864,234.7C960,245,1056,235,1152,213.3C1248,192,1344,160,1392,144L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
                    </svg>
                </div>
                
                <div className="relative z-10 w-full max-w-[440px] px-4 py-8">
                    <div className="auth-container w-full shadow-sm">
            <div className="auth-header mb-6">
                <h1 className="heading-1 text-balance text-3xl font-bold leading-tight text-slate-900 md:text-4xl">
                Sign in or <br />
                create an account
                </h1>
                <p className="auth-description mt-3 text-sm leading-relaxed text-slate-700 md:text-base">
                This email will be used to sign into Hari Vishnu’s site.
                <br />
                By submitting your email, you agree to our Terms and Privacy
                Policy.
                </p>
            </div>

            <h2 className="heading-2 mb-4 text-2xl font-semibold text-slate-900">
                {!emailChecked ? "Login or Register" : userExists ? "Login" : "Register"}
            </h2>

            {!emailChecked && (
                <div className="email-input">
                <div className={`email-field-wrapper${emailFocused ? ' is-focused' : ''}${emailError ? ' has-error' : (email && !emailError ? ' has-success' : '')}`}>
                    <div className="email-field-inner">
                        <label className={`email-field-label${(emailFocused || email) ? ' floating' : ''}`}>
                            Email Address
                        </label>
                        <i className="fi fi-tr-envelope email-field-icon"></i>
                        <span className="email-field-sep">|</span>
                        <input
                            ref={emailInputRef}
                            type="email"
                            placeholder=""
                            value={email}
                            onChange={(e) => {
                                const val = e.target.value.toLowerCase();
                                setEmail(val);
                                validateEmail(val);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && email && !emailError && !emailChecking) {
                                    checkEmailExists();
                                }
                            }}
                            onFocus={() => setEmailFocused(true)}
                            onBlur={() => setEmailFocused(false)}
                            required
                        />
                    </div>
                    {emailError ? (
                        <div className="email-field-message error-msg">
                            <span>⚠</span> {emailError}
                        </div>
                    ) : email ? (
                        <div className="email-field-message success-msg">
                            <span>✓</span> Great!
                        </div>
                    ) : null}
                </div>
                <button
                    tabIndex={-1}
                    className={`email-continue-btn ${emailChecking ? "auth-button-loading" : ""}`}
                    onClick={checkEmailExists}
                    disabled={!email || emailError || emailChecking}
                >
                    {emailChecking ? (
                        <span className="auth-spinner-container">
                            <span className="auth-spinner"></span>
                        </span>
                    ) : (
                        "Continue"
                    )}
                </button>
                </div>
            )}

            {emailChecked && (
                <>
                <div className="email-display">
                    <span>{email}</span>
                    <div className="email-edit-button">
                    <button
                        tabIndex={-1}
                        className="email-goback-btn"
                        onClick={() => setEmailChecked(false)}
                    >
                        Edit
                    </button>
                    </div>
                </div>

                <form onSubmit={handleAuthSubmit}>
                    {!userExists && (
                        <>
                        <div className={`email-field-wrapper${nameFocused ? ' is-focused' : ''}${nameError ? ' has-error' : (name && !nameError ? ' has-success' : '')}`}>
                            <div className="email-field-inner">
                                <label className={`email-field-label${(nameFocused || name) ? ' floating' : ''}`}>
                                    Full Name
                                </label>
                                <i className="fi fi-rr-id-card-clip-alt email-field-icon"></i>
                                <span className="email-field-sep">|</span>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    onFocus={() => setNameFocused(true)}
                                    onBlur={() => setNameFocused(false)}
                                    required
                                />
                            </div>
                            {nameError && (
                                <div className="email-field-message error-msg">
                                    <span>⚠</span> {nameError}
                                </div>
                            )}
                        </div>
                        </>
                    )}
                    <div className={`email-field-wrapper${passwordFocused ? ' is-focused' : ''}${passwordError ? ' has-error' : (password && !passwordError ? ' has-success' : '')}`}>
                        <div className="email-field-inner">
                            <label className={`email-field-label${(passwordFocused || password) ? ' floating' : ''}`}>
                                {userExists ? "Password" : "Create Password"}
                            </label>
                            <i className="fi fi-rr-lock email-field-icon"></i>
                            <span className="email-field-sep">|</span>
                            <input
                                ref={passwordInputRef}
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    validatePassword(e.target.value);
                                    setShowPasswordRules(e.target.value.length > 0);
                                }}
                                onFocus={() => setPasswordFocused(true)}
                                onBlur={() => setPasswordFocused(false)}
                                required
                            />
                        </div>
                    </div>
                    <div className="password-toggle-section">
                        <label className="password-toggle-label">
                            <input
                                type="checkbox"
                                checked={showPassword}
                                onChange={(e) => setShowPassword(e.target.checked)}
                                className="password-toggle-checkbox"
                            />
                            <span className="custom-checkbox-visual"></span>
                            <span className="password-toggle-text">Show password</span>
                        </label>
                    </div>
                    {userExists && (
                        <div className="forgot-password-link">
                            <a href="/forgot-password" onClick={(e) => {
                                e.preventDefault();
                                navigate("/forgot-password");
                            }}>
                                Forgot Password?
                            </a>
                        </div>
                    )}
                    {!userExists && (
                        <>
                        {showPasswordRules && (
                        <div className="password-rules">
                            <div className={`password-rule ${isMinLength ? "valid" : "invalid"}`}>
                                <span className="rule-icon">{isMinLength ? "✓" : "✕"}</span>
                                At least 8 characters
                            </div>
                            <div className={`password-rule ${hasNumber ? "valid" : "invalid"}`}>
                                <span className="rule-icon">{hasNumber ? "✓" : "✕"}</span>
                                Contains a number
                            </div>
                            <div className={`password-rule ${hasSpecialChar ? "valid" : "invalid"}`}>
                                <span className="rule-icon">{hasSpecialChar ? "✓" : "✕"}</span>
                                Contains a special character
                            </div>
                        </div>
                        )}
                        </>
                    )}

                    <button 
                        type="submit" 
                        disabled={!isFormValid || loading || success}
                        className={`${loading ? "auth-button-loading" : ""} ${success ? "success-state" : ""}`}
                        style={success ? { backgroundColor: "#4caf50", borderColor: "#4caf50", color: "white" } : {}}
                    >
                        {loading ? (
                            <span className="auth-spinner-container">
                                <span className="auth-spinner"></span>
                            </span>
                        ) : success ? (
                            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                                <i className="fi fi-rr-check-circle" style={{ display: "flex", alignItems: "center", marginTop: "2px" }}></i> Success
                            </span>
                        ) : (
                            userExists ? "Login" : "Register"
                        )}
                    </button>
                </form>
                </>
            )}

            <div className="divider my-5">
                <hr />
                <span>or</span>
                <hr />
            </div>

            <div id="googleAuthDiv" className="mt-5" style={{ marginTop: "20px" }}></div>
            </div>
            </div>
            </main>
        </div>
        <Footer />
        </>
    );
}
